import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import CompanyOnboardingService from "@/services/CompanyOnboardingService";
import UserIdentitiesService from "@/services/UserIdentitiesService";
import type { ProfessionCatalogsDto } from "@/services/UserIdentitiesService";
import type { CompanyDetailsDraftDto, CompanyDocumentCategory, CompanyDocumentDto, SelectableRepresentativeDto } from "@/services/CompanyOnboardingService";
import { IdentitiesPath } from "@/screen/Identities/identitiesRoutes";
import { CompanyKycPath } from "@/screen/Companies/companiesRoutes";
import type { CompanyDetailsNavigationState } from "@/screen/Companies/companiesRoutes";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import { toCompanyOnboardingErrorMessages } from "@/screen/Companies/companyOnboardingErrors";
import { toCnaeOptions, buildSaveCompanyDetailsRequest, getDocumentFileError, getMissingCompanyFields, getMissingDocumentMessages, getRepresentativeIssues, isAddressComplete, toCompanyDetailsFormData, toRepresentativeRow } from "@/screen/Companies/CompanyDetails/companyDetailsForm";
import type { CnaeOption, CompanyAddressFormData, CompanyDetailsFormData, CompanyDetailsTextField, CompanyRepresentativeFormRow } from "@/screen/Companies/CompanyDetails/companyDetailsForm";

//  loading  → cargando la empresa y los representantes seleccionables
//  error    → no hay empresa (sin id en la navegación, de otro usuario...) o no se ha podido cargar
//  editing  → borrador editable (NEW)
//  readOnly → la empresa ya se ha enviado: se enseña sin poder modificarla
export type CompanyDetailsPhase = "loading" | "error" | "editing" | "readOnly";

export type AddressKind = "fiscalAddress" | "registeredAddress";

export function useCompanyDetailsController()
{
    const history = useHistory();
    const location = useLocation<CompanyDetailsNavigationState | undefined>();
    const companyIdentityId = Number(location.state?.companyIdentityId ?? 0);

    const [phase, setPhase] = useState<CompanyDetailsPhase>("loading");
    const [loadError, setLoadError] = useState<string | null>(null);
    const [formData, setFormData] = useState<CompanyDetailsFormData | null>(null);
    const [documents, setDocuments] = useState<CompanyDocumentDto[]>([]);
    const [selectable, setSelectable] = useState<SelectableRepresentativeDto[]>([]);
    const [declarationAccepted, setDeclarationAccepted] = useState<boolean>(false);
    const [showMissing, setShowMissing] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [uploadingCategory, setUploadingCategory] = useState<CompanyDocumentCategory | null>(null);
    const [messages, setMessages] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [cnaeOptions, setCnaeOptions] = useState<CnaeOption[]>([]);
    const [cnaeLoading, setCnaeLoading] = useState<boolean>(true);
    const [cnaeError, setCnaeError] = useState<string | null>(null);

    // Catálogo de CNAE de Omnia (el mismo que usa el alta de autónomos). No bloquea la pantalla si falla.
    useEffect(() =>
    {
        let cancelled = false;
        UserIdentitiesService.getProfessionCatalogs("cnae").then((response) =>
        {
            if(cancelled)
            {
                return;
            }
            const options = response.success ? toCnaeOptions((response.data as ProfessionCatalogsDto | null)?.cnaes) : [];
            setCnaeOptions(options);
            setCnaeError(options.length > 0 ? null : CompanyOnboardingStrings.cnaeLoadError);
            setCnaeLoading(false);
        });

        return () =>
        {
            cancelled = true;
        };
    }, []);

    useEffect(() =>
    {
        if(!(companyIdentityId > 0))
        {
            setLoadError(CompanyOnboardingStrings.detailsNoCompany);
            setPhase("error");
            return;
        }

        let cancelled = false;
        Promise.all([CompanyOnboardingService.getCompanyDraft(companyIdentityId), CompanyOnboardingService.getSelectableRepresentatives()]).then(([draftResponse, selectableResponse]) =>
        {
            if(cancelled)
            {
                return;
            }
            if(!draftResponse.success || !draftResponse.data)
            {
                setLoadError(toCompanyOnboardingErrorMessages(draftResponse.errors, CompanyOnboardingStrings.detailsLoadError)[0]);
                setPhase("error");
                return;
            }

            const draft = draftResponse.data as CompanyDetailsDraftDto;
            setFormData(toCompanyDetailsFormData(draft));
            setDocuments(draft.documents ?? []);
            setSelectable(selectableResponse.success && Array.isArray(selectableResponse.data) ? selectableResponse.data : []);
            setPhase(draft.status === "NEW" ? "editing" : "readOnly");
        });

        return () =>
        {
            cancelled = true;
        };
    }, [companyIdentityId]);

    const clearFeedback = useCallback(() =>
    {
        setMessages([]);
        setErrors([]);
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    {
        const name = e.target.name as CompanyDetailsTextField | "digitalSignature";
        const value = e.target.value;
        setFormData((prev) => (prev ? {...prev, [name]: value} : prev));
        clearFeedback();
    }, [clearFeedback]);

    const handleCnaeChange = useCallback((value: string) =>
    {
        setFormData((prev) => (prev ? {...prev, cnae: value} : prev));
        clearFeedback();
    }, [clearFeedback]);

    const handleAddressChange = useCallback((kind: AddressKind, field: keyof CompanyAddressFormData, value: string) =>
    {
        setFormData((prev) => (prev ? {...prev, [kind]: {...prev[kind], [field]: value}} : prev));
        clearFeedback();
    }, [clearFeedback]);

    const handleRegisteredSameChange = useCallback((checked: boolean) =>
    {
        setFormData((prev) => (prev ? {...prev, registeredAddressSameAsFiscal: checked} : prev));
        clearFeedback();
    }, [clearFeedback]);

    const handleAddRepresentative = useCallback((userIdentityId: number) =>
    {
        const candidate = selectable.find((item) => item.userIdentityId === userIdentityId);
        if(!candidate)
        {
            return;
        }
        setFormData((prev) =>
        {
            if(!prev || prev.representatives.some((row) => row.userIdentityId === userIdentityId))
            {
                return prev;
            }
            return {...prev, representatives: [...prev.representatives, toRepresentativeRow(candidate)]};
        });
        clearFeedback();
    }, [selectable, clearFeedback]);

    const handleRemoveRepresentative = useCallback((userIdentityId: number) =>
    {
        setFormData((prev) => (prev ? {...prev, representatives: prev.representatives.filter((row) => row.userIdentityId !== userIdentityId)} : prev));
        clearFeedback();
    }, [clearFeedback]);

    const handleRepresentativeChange = useCallback((userIdentityId: number, field: "role" | "sign" | "ownership", value: string) =>
    {
        setFormData((prev) =>
        {
            if(!prev)
            {
                return prev;
            }
            const representatives = prev.representatives.map((row): CompanyRepresentativeFormRow =>
                (row.userIdentityId === userIdentityId ? {...row, [field]: value} : row));
            return {...prev, representatives};
        });
        clearFeedback();
    }, [clearFeedback]);

    const handleUploadDocuments = useCallback(async (category: CompanyDocumentCategory, files: File[]) =>
    {
        clearFeedback();
        // El CIF es un único documento: solo cuenta el primero
        const toUpload = category === "TaxIdCard" ? files.slice(0, 1) : files;
        const fileErrors = toUpload.map((file) => getDocumentFileError(file)).filter((message): message is string => message !== null);
        if(fileErrors.length > 0)
        {
            setErrors(Array.from(new Set(fileErrors)));
            return;
        }

        setUploadingCategory(category);
        const uploadErrors: string[] = [];
        for(const file of toUpload)
        {
            const response = await CompanyOnboardingService.uploadCompanyDocument(companyIdentityId, category, file);
            if(response.success && response.data)
            {
                const uploaded = response.data as CompanyDocumentDto;
                setDocuments((prev) => [...prev.filter((document) => category !== "TaxIdCard" || document.category !== "TaxIdCard"), uploaded]);
            }
            else
            {
                uploadErrors.push(...toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.documentGenericError));
            }
        }
        setUploadingCategory(null);
        setErrors(Array.from(new Set(uploadErrors)));
    }, [companyIdentityId, clearFeedback]);

    const handleDeleteDocument = useCallback(async (documentId: number) =>
    {
        clearFeedback();
        const response = await CompanyOnboardingService.deleteCompanyDocument(companyIdentityId, documentId);
        if(response.success || response.errors?.some((error) => error.code === "DOCUMENT_NOT_FOUND"))
        {
            setDocuments((prev) => prev.filter((document) => document.id !== documentId));
            return;
        }
        setErrors(toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.documentGenericError));
    }, [companyIdentityId, clearFeedback]);

    const save = useCallback(async (complete: boolean) =>
    {
        if(!formData)
        {
            return;
        }

        clearFeedback();
        setIsSaving(true);
        const response = await CompanyOnboardingService.saveCompanyDetails(companyIdentityId, buildSaveCompanyDetailsRequest(formData, complete));
        setIsSaving(false);

        if(response.success)
        {
            if(complete)
            {
                // Paso 3 completo: continúa al KYC con la misma empresa (id por el state, no por la URL)
                history.push(CompanyKycPath, { companyIdentityId } as CompanyDetailsNavigationState);
                return;
            }
            setMessages([CompanyOnboardingStrings.draftSaved]);
            return;
        }
        // Al continuar con datos que faltan el backend guarda igualmente el borrador y devuelve lo que falta
        setErrors(toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.detailsGenericError));
    }, [formData, companyIdentityId, clearFeedback, history]);

    // Lo que falta para continuar, con el mismo criterio que el backend
    const missingFields = useMemo(() => (formData ? getMissingCompanyFields(formData) : []), [formData]);
    const pendingIssues = useMemo(() =>
    {
        if(!formData)
        {
            return [];
        }
        const issues: string[] = [];
        if(missingFields.length > 0)
        {
            issues.push("Completa los campos obligatorios marcados.");
        }
        if(!isAddressComplete(formData.fiscalAddress))
        {
            issues.push("Completa el domicilio fiscal.");
        }
        if(!formData.registeredAddressSameAsFiscal && !isAddressComplete(formData.registeredAddress))
        {
            issues.push("Completa el domicilio social.");
        }
        return [...issues, ...getMissingDocumentMessages(documents), ...getRepresentativeIssues(formData.representatives)];
    }, [formData, missingFields, documents]);

    const handleSaveDraft = useCallback(() => save(false), [save]);

    const handleContinue = useCallback(async () =>
    {
        setShowMissing(true);
        if(pendingIssues.length > 0)
        {
            setMessages([]);
            setErrors([CompanyOnboardingStrings.detailsIncomplete, ...pendingIssues]);
            window.scrollTo(0, 0);
            return;
        }
        await save(true);
    }, [pendingIssues, save]);

    const goToIdentities = useCallback(() =>
    {
        history.push(IdentitiesPath);
    }, [history]);

    const selectedIds = formData?.representatives.map((row) => row.userIdentityId) ?? [];

    return{
        phase,
        loadError,
        formData,
        documents,
        isEditable: phase === "editing",
        availableRepresentatives: selectable.filter((item) => !selectedIds.includes(item.userIdentityId)),
        hasSelectableRepresentatives: selectable.length > 0,
        borrowedIds: selectable.filter((item) => !item.isOwn).map((item) => item.userIdentityId),
        declarationAccepted,
        setDeclarationAccepted,
        isMissing: (field: CompanyDetailsTextField): boolean => showMissing && missingFields.includes(field),
        isSaving,
        uploadingCategory,
        messages,
        errors,
        canContinue: phase === "editing" && declarationAccepted && !isSaving && uploadingCategory === null,
        cnaeOptions,
        cnaeLoading,
        cnaeError,
        handleChange,
        handleCnaeChange,
        handleAddressChange,
        handleRegisteredSameChange,
        handleAddRepresentative,
        handleRemoveRepresentative,
        handleRepresentativeChange,
        handleUploadDocuments,
        handleDeleteDocument,
        handleSaveDraft,
        handleContinue,
        goToIdentities,
    };
}
